import React, { useState, useCallback, useEffect } from 'react';
import { 
  Plus, Trash2, ChevronRight, ChevronLeft, Copy, Clipboard, 
  Bookmark, ChevronDown, Undo2, Redo2, Move, ArrowUpRight,
  ArrowDownRight, FileSpreadsheet, Download, BarChart3, Eye,
  Settings, MoreHorizontal, Layers, FolderTree, GitCompare, Scissors,
  Diamond, Link2, Unlink, Calculator, Upload, FileDown, Percent, Paperclip, Plug
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PullAllButton } from './PullAllButton';

const WBSToolbar = ({
  gridRef,
  tasks,
  project,
  scheduleId,
  linkedActivityCount = 0,
  onPullAllLinks,
  baselines,
  activeBaseline,
  selectedTaskId,
  selectedTasks,
  clipboard,
  rowClipboard,
  onAddTask,
  onDeleteTask,
  onIndent,
  onOutdent,
  onCopyTask,
  onCutTask,
  onPasteTask,
  onCopyRows,
  onPasteRowsBelow,
  onInsertRow,
  onMoveToParent,
  onMoveToChild,
  onCreateBaseline,
  onSetActiveBaseline,
  onDeleteBaseline,
  onExportBaseline,
  onCompareBaselines,
  onRecalculate,
  onUndo,
  onRedo,
  onToggleMilestone,
  onLinkTasks,
  onUnlinkTasks,
  canUndo,
  canRedo,
  onOpenImportExport,
  onOpenConnectorImport,
  onOpenVersions,
  onAutoCalculateWeightage,
  onAutoCalculateDuration = null,
  onDeleteEntireSchedule = null,
  onBulkAttachDocs = null,
  userRole = 'planner' // viewer, planner, pmo
}) => {
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [baselineDialogOpen, setBaselineDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveMode, setMoveMode] = useState('parent'); // parent or child
  const [targetTaskId, setTargetTaskId] = useState('');
  const [compareBaseline1, setCompareBaseline1] = useState('');
  const [compareBaseline2, setCompareBaseline2] = useState('current');
  const [insertRowsDialogOpen, setInsertRowsDialogOpen] = useState(false);
  const [insertRowCount, setInsertRowCount] = useState(1);
  const [insertPosition, setInsertPosition] = useState('below');
  
  // Baseline form state
  const [baselineForm, setBaselineForm] = useState({
    name: '',
    freezeAll: true,
    includeCompleted: true,
    includeSummary: true
  });

  // Task to delete info
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [childCount, setChildCount] = useState(0);

  // Get selected task
  const selectedTask = tasks?.find(t => t.id === selectedTaskId);
  const isMultiSelect = selectedTasks && selectedTasks.length > 1;
  const hasSelection = selectedTaskId || (selectedTasks && selectedTasks.length > 0);

  // Check permissions
  const canEdit = userRole === 'planner' || userRole === 'pmo';
  const canManageBaseline = userRole === 'planner' || userRole === 'pmo';
  const isViewer = userRole === 'viewer';

  // Calculate child count for delete confirmation
  const getChildCount = useCallback((taskId) => {
    if (!tasks) return 0;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return 0;
    const wbsPrefix = task.wbs_code + '.';
    return tasks.filter(t => t.wbs_code?.startsWith(wbsPrefix)).length;
  }, [tasks]);

  // Handle delete click
  const handleDeleteClick = () => {
    if (!selectedTaskId) {
      toast.error('No task selected');
      return;
    }
    const task = tasks.find(t => t.id === selectedTaskId);
    if (!task) return;
    
    const children = getChildCount(selectedTaskId);
    setTaskToDelete(task);
    setChildCount(children);
    
    if (children > 0) {
      setDeleteDialogOpen(true);
    } else {
      // Direct delete for leaf tasks
      onDeleteTask(selectedTaskId);
      toast.success('Task deleted');
    }
  };

  // Confirm delete
  const confirmDelete = () => {
    if (taskToDelete) {
      onDeleteTask(taskToDelete.id);
      toast.success(`Task and ${childCount} child tasks deleted`);
    }
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  // Handle baseline creation
  const handleCreateBaseline = () => {
    const nextNumber = (baselines?.length || 0) + 1;
    setBaselineForm({
      name: `BL-${nextNumber}`,
      freezeAll: true,
      includeCompleted: true,
      includeSummary: true
    });
    setBaselineDialogOpen(true);
  };

  const confirmCreateBaseline = () => {
    onCreateBaseline(baselineForm);
    toast.success(`Baseline "${baselineForm.name}" created successfully`);
    setBaselineDialogOpen(false);
  };

  // Handle move to parent/child
  const handleMoveClick = (mode) => {
    if (!selectedTaskId) {
      toast.error('No task selected');
      return;
    }
    setMoveMode(mode);
    setTargetTaskId('');
    setMoveDialogOpen(true);
  };

  const confirmMove = () => {
    if (!targetTaskId) {
      toast.error('Please select a target task');
      return;
    }
    if (moveMode === 'parent') {
      onMoveToParent(selectedTaskId, targetTaskId);
      toast.success('Task moved to new parent');
    } else {
      onMoveToChild(selectedTaskId, targetTaskId);
      toast.success('Task moved as child');
    }
    setMoveDialogOpen(false);
  };

  // Handle Compare Baselines
  const handleCompareBaselines = () => {
    if (baselines?.length < 1) {
      toast.error('No baselines available to compare');
      return;
    }
    setCompareBaseline1(baselines[0]?.baseline_number?.toString() || '');
    setCompareBaseline2('current');
    setCompareDialogOpen(true);
  };

  const confirmCompareBaselines = () => {
    if (!compareBaseline1) {
      toast.error('Please select a baseline');
      return;
    }
    if (onCompareBaselines) {
      onCompareBaselines(parseInt(compareBaseline1), compareBaseline2 === 'current' ? null : parseInt(compareBaseline2));
    }
    setCompareDialogOpen(false);
  };

  // Handle Cut
  const handleCutTask = () => {
    if (!selectedTaskId) {
      toast.error('No task selected');
      return;
    }
    if (onCutTask) {
      onCutTask(selectedTaskId);
    }
  };

  // Handle Link Tasks
  const handleLinkTasks = () => {
    if (selectedTasks?.length < 2) {
      toast.error('Select at least 2 tasks to link');
      return;
    }
    if (onLinkTasks) {
      onLinkTasks(selectedTasks);
    }
  };

  // Handle Unlink Tasks
  const handleUnlinkTasks = () => {
    if (!selectedTaskId) {
      toast.error('No task selected');
      return;
    }
    if (onUnlinkTasks) {
      onUnlinkTasks(selectedTaskId);
    }
  };

  // Handle Toggle Milestone
  const handleToggleMilestone = () => {
    if (!selectedTaskId) {
      toast.error('No task selected');
      return;
    }
    if (onToggleMilestone) {
      onToggleMilestone(selectedTaskId);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isViewer) return;
      
      // Check if we're in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === 'Insert') {
        e.preventDefault();
        onAddTask(selectedTaskId);
      } else if (e.key === 'Delete' && hasSelection) {
        e.preventDefault();
        handleDeleteClick();
      } else if (e.key === 'Tab' && !e.shiftKey && hasSelection) {
        e.preventDefault();
        onIndent(selectedTaskId);
      } else if (e.key === 'Tab' && e.shiftKey && hasSelection) {
        e.preventDefault();
        onOutdent(selectedTaskId);
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'c' && hasSelection) {
        // Let AG Grid handle this
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) {
        // Let AG Grid handle this
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (canUndo) onUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        if (canRedo) onRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTaskId, hasSelection, clipboard, canUndo, canRedo, isViewer, onAddTask, onIndent, onOutdent, onUndo, onRedo]);

  // Get available parent tasks for move dialog
  const availableParents = tasks?.filter(t => {
    if (!selectedTask) return false;
    if (t.id === selectedTaskId) return false;
    // Can't move to own descendants
    if (t.wbs_code?.startsWith(selectedTask.wbs_code + '.')) return false;
    return true;
  }) || [];

  return (
    <TooltipProvider>
      <div className="wbs-toolbar flex items-center gap-1 px-3 py-2 bg-[#0a0f1e] border-b border-slate-700/50 sticky top-0 z-20 overflow-x-auto whitespace-nowrap">
        
        {/* Left Section - Task Actions */}
        <div className="flex items-center gap-1 pr-3 border-r border-slate-700/50">
          {/* Add Task with Dropdown for Insert Options */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={isViewer}
                    className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                    data-testid="toolbar-new-task"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    <span className="text-xs">New Task</span>
                    <ChevronDown className="w-3 h-3 ml-1 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Insert new task(s)</p>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="bg-slate-800 border-slate-700 w-52">
              <DropdownMenuLabel className="text-slate-400 text-xs">Insert Task</DropdownMenuLabel>
              <DropdownMenuItem 
                onClick={() => {
                  if (selectedTaskId && onInsertRow) {
                    onInsertRow(selectedTaskId, 'below', null, 1);
                  } else {
                    onAddTask(selectedTaskId);
                  }
                }}
                className="text-slate-200 hover:bg-slate-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Insert Below
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => {
                  if (selectedTaskId && onInsertRow) {
                    onInsertRow(selectedTaskId, 'above', null, 1);
                  } else {
                    toast.error('Please select a task first');
                  }
                }}
                disabled={!selectedTaskId}
                className="text-slate-200 hover:bg-slate-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Insert Above
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem 
                onClick={() => setInsertRowsDialogOpen(true)}
                disabled={!selectedTaskId}
                className="text-slate-200 hover:bg-slate-700"
              >
                <Layers className="w-4 h-4 mr-2" />
                Insert Multiple Rows...
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuLabel className="text-slate-400 text-xs">Clipboard</DropdownMenuLabel>
              <DropdownMenuItem 
                onClick={() => onCopyRows && onCopyRows()}
                disabled={!hasSelection}
                className="text-slate-200 hover:bg-slate-700"
              >
                <Copy className="w-4 h-4 mr-2 text-[#1E4E8C]" />
                Copy Row(s) to Clipboard
                {rowClipboard?.length > 0 && <span className="ml-auto text-[#1E4E8C] text-xs">{rowClipboard.length}</span>}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => {
                  if (selectedTaskId && onPasteRowsBelow) {
                    onPasteRowsBelow(selectedTaskId);
                  } else {
                    toast.error('Please select a target task');
                  }
                }}
                disabled={!selectedTaskId || !rowClipboard || rowClipboard.length === 0}
                className="text-slate-200 hover:bg-slate-700"
              >
                <Clipboard className="w-4 h-4 mr-2" />
                Paste Rows Below (Insert)
                {rowClipboard?.length > 0 && <span className="ml-auto text-[#1E4E8C] text-xs">{rowClipboard.length}</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Delete */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDeleteClick}
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                <span className="text-xs">Delete</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Delete selected task (Del)</p>
            </TooltipContent>
          </Tooltip>

          {/* Delete Entire Schedule (compact icon-only to avoid toolbar overflow) */}
          {onDeleteEntireSchedule && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onDeleteEntireSchedule}
                  disabled={isViewer || !tasks || tasks.length === 0}
                  className="h-8 w-8 p-0 text-red-300 hover:text-red-200 hover:bg-red-500/15 shrink-0 relative"
                  data-testid="toolbar-delete-entire-schedule"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="absolute -top-0.5 -right-0.5 text-[9px] leading-none font-bold text-red-400 bg-[#0a0f1e] rounded-full w-3 h-3 flex items-center justify-center">×</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Delete the entire macro schedule (clears every task)</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Bulk Attach Documents (multi-select) */}
          {onBulkAttachDocs && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onBulkAttachDocs}
                  disabled={isViewer || !selectedTasks || selectedTasks.length < 2}
                  className="h-8 w-8 p-0 text-sky-300 hover:text-sky-200 hover:bg-sky-500/15 shrink-0 relative"
                  data-testid="toolbar-bulk-attach-docs"
                >
                  <Paperclip className="w-4 h-4" />
                  {selectedTasks && selectedTasks.length > 1 && (
                    <span className="absolute -top-0.5 -right-0.5 text-[9px] leading-none font-bold text-white bg-sky-500 rounded-full min-w-3 h-3 px-0.5 flex items-center justify-center">
                      {selectedTasks.length > 99 ? '99+' : selectedTasks.length}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>
                  {(!selectedTasks || selectedTasks.length < 2)
                    ? 'Select 2+ activities (Ctrl-click / Shift-click) to bulk-attach documents'
                    : `Bulk-attach documents to ${selectedTasks.length} selected activities`}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Separator */}
          <div className="w-px h-6 bg-slate-700/50 mx-1" />

          {/* Copy */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onCopyTask(selectedTaskId)}
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                data-testid="toolbar-copy"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Copy task (Ctrl+C)</p>
            </TooltipContent>
          </Tooltip>

          {/* Cut */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCutTask}
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                data-testid="toolbar-cut"
              >
                <Scissors className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Cut task (Ctrl+X)</p>
            </TooltipContent>
          </Tooltip>

          {/* Paste */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onPasteTask(selectedTaskId)}
                disabled={isViewer || !clipboard}
                className={`h-8 px-2 hover:bg-slate-700/50 ${clipboard ? 'text-[#1E4E8C]' : 'text-slate-500'}`}
                data-testid="toolbar-paste"
              >
                <Clipboard className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Paste task (Ctrl+V) {clipboard ? '- Task in clipboard' : ''}</p>
            </TooltipContent>
          </Tooltip>

          {/* Separator */}
          <div className="w-px h-6 bg-slate-700/50 mx-1" />

          {/* Link Tasks */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleLinkTasks}
                disabled={isViewer || (selectedTasks?.length || 0) < 2}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                data-testid="toolbar-link"
              >
                <Link2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Link selected tasks (FS dependency)</p>
            </TooltipContent>
          </Tooltip>

          {/* Unlink Tasks */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleUnlinkTasks}
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                data-testid="toolbar-unlink"
              >
                <Unlink className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Remove all predecessors</p>
            </TooltipContent>
          </Tooltip>

          {/* Toggle Milestone */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleToggleMilestone}
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-amber-400 hover:bg-amber-500/10"
                data-testid="toolbar-milestone"
              >
                <Diamond className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Toggle milestone</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Center Section - Hierarchy Actions */}
        <div className="flex items-center gap-1 px-3 border-r border-slate-700/50">
          {/* Indent */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onIndent(selectedTaskId)}
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                data-testid="toolbar-indent"
              >
                <ChevronRight className="w-4 h-4 mr-1" />
                <span className="text-xs">Indent</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Make child of above task (Tab)</p>
            </TooltipContent>
          </Tooltip>

          {/* Outdent */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOutdent(selectedTaskId)}
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                data-testid="toolbar-outdent"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="text-xs">Outdent</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Promote to parent level (Shift+Tab)</p>
            </TooltipContent>
          </Tooltip>

          {/* Move Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                disabled={isViewer || !hasSelection}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
              >
                <Move className="w-4 h-4 mr-1" />
                <span className="text-xs">Move</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-800 border-slate-700">
              <DropdownMenuItem onClick={() => handleMoveClick('parent')} className="text-slate-200 hover:bg-slate-700">
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Move to Parent...
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleMoveClick('child')} className="text-slate-200 hover:bg-slate-700">
                <ArrowDownRight className="w-4 h-4 mr-2" />
                Move as Child...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Undo/Redo */}
          <div className="w-px h-6 bg-slate-700/50 mx-1" />
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onUndo}
                disabled={!canUndo}
                className="h-8 px-2 text-slate-400 hover:text-white hover:bg-slate-700/50 disabled:opacity-30"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Undo (Ctrl+Z)</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onRedo}
                disabled={!canRedo}
                className="h-8 px-2 text-slate-400 hover:text-white hover:bg-slate-700/50 disabled:opacity-30"
              >
                <Redo2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Redo (Ctrl+Y)</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Right Section - Baseline Management */}
        <div className="flex items-center gap-1 px-3 ml-auto">
          {/* Active Baseline Indicator */}
          {activeBaseline && (
            <Badge variant="outline" className="h-6 px-2 border-[#1E4E8C]/50 text-[#1E4E8C] text-xs mr-2">
              <Bookmark className="w-3 h-3 mr-1" />
              {activeBaseline.name}
            </Badge>
          )}

          {/* Create Baseline */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCreateBaseline}
                disabled={!canManageBaseline}
                className="h-8 px-2 text-slate-300 hover:text-[#1E4E8C] hover:bg-[#1E4E8C]/10"
              >
                <Bookmark className="w-4 h-4 mr-1.5" />
                <span className="text-xs">Create Baseline</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Create new baseline snapshot</p>
            </TooltipContent>
          </Tooltip>

          {/* Baseline Management Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                disabled={!canManageBaseline || !baselines?.length}
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
              >
                <Layers className="w-4 h-4 mr-1" />
                <span className="text-xs">Baselines</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-800 border-slate-700 min-w-48">
              <DropdownMenuLabel className="text-slate-400 text-xs">Set Active Baseline</DropdownMenuLabel>
              {baselines?.map(bl => (
                <DropdownMenuItem 
                  key={bl.id}
                  onClick={() => onSetActiveBaseline(bl.id)}
                  className={`text-slate-200 hover:bg-slate-700 ${activeBaseline?.id === bl.id ? 'bg-[#1E4E8C]/20' : ''}`}
                >
                  <Bookmark className={`w-4 h-4 mr-2 ${activeBaseline?.id === bl.id ? 'text-[#1E4E8C]' : ''}`} />
                  {bl.name}
                  {activeBaseline?.id === bl.id && (
                    <Badge className="ml-auto text-xs bg-[#1E4E8C]/20 text-[#1E4E8C]">Active</Badge>
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem onClick={handleCompareBaselines} className="text-slate-200 hover:bg-slate-700">
                <GitCompare className="w-4 h-4 mr-2" />
                Compare Baselines
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportBaseline} className="text-slate-200 hover:bg-slate-700">
                <Download className="w-4 h-4 mr-2" />
                Export Baseline
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem onClick={() => onDeleteBaseline(activeBaseline?.id)} className="text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Baseline
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Recalculate Button */}
          <div className="w-px h-6 bg-slate-700/50 mx-1" />
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onRecalculate}
                className="h-8 px-2 text-slate-300 hover:text-emerald-400 hover:bg-emerald-500/10"
                data-testid="toolbar-recalculate"
              >
                <FolderTree className="w-4 h-4 mr-1.5" />
                <span className="text-xs">Recalculate</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Recalculate schedule (CPM)</p>
            </TooltipContent>
          </Tooltip>

          {/* Versions Button */}
          {onOpenVersions && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onOpenVersions}
                  className="h-8 px-2 text-slate-300 hover:text-blue-400 hover:bg-blue-500/10"
                  data-testid="toolbar-versions"
                >
                  <GitCompare className="w-4 h-4 mr-1.5" />
                  <span className="text-xs">Versions</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Compare uploaded schedule versions (Excel · MSP · XER · XML)</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Pull progress from every linked discipline schedule at once */}
          {scheduleId && (
            <PullAllButton scheduleId={scheduleId} linkedCount={linkedActivityCount} onDone={onPullAllLinks} />
          )}

          {/* Auto Calculate Weightage Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={onAutoCalculateWeightage}
                className="h-8 px-2 text-slate-300 hover:text-amber-400 hover:bg-amber-500/10"
                data-testid="toolbar-auto-weightage"
              >
                <Percent className="w-4 h-4 mr-1.5" />
                <span className="text-xs">Auto Weight</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Auto-calculate weight from duration (sibling groups sum to 100%)</p>
            </TooltipContent>
          </Tooltip>

          {/* Reverse: Auto-Duration from Weight — the mirror operation of
              Auto Weight. Planners who own the weight distribution can
              press this to redistribute each sibling group's total
              duration proportionally to the user-entered weights. */}
          {onAutoCalculateDuration && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onAutoCalculateDuration}
                  className="h-8 px-2 text-slate-300 hover:text-sky-400 hover:bg-sky-500/10"
                  data-testid="toolbar-auto-duration"
                >
                  <Calculator className="w-4 h-4 mr-1.5" />
                  <span className="text-xs">Auto Duration</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Auto-calculate duration from weight — each sibling group's total duration is redistributed by the user-entered weight %.</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Import/Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-slate-300 hover:text-white hover:bg-slate-700/50"
                data-testid="toolbar-import-export"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                <span className="text-xs">Import/Export</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-800 border-slate-700 min-w-48">
              <DropdownMenuItem 
                onClick={() => onOpenImportExport?.('export')} 
                className="text-slate-200 hover:bg-slate-700"
                data-testid="menu-export-excel"
              >
                <FileDown className="w-4 h-4 mr-2 text-emerald-400" />
                Export Schedule (Excel · XER · MPP)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onOpenImportExport?.('import')} 
                className="text-slate-200 hover:bg-slate-700"
                data-testid="menu-import-excel"
              >
                <Upload className="w-4 h-4 mr-2 text-blue-400" />
                Import Schedule (Excel · XER · MPP)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onOpenConnectorImport?.()}
                className="text-slate-200 hover:bg-slate-700"
                data-testid="menu-import-connector"
              >
                <Plug className="w-4 h-4 mr-2 text-emerald-400" />
                Import from connector (P6 · MS Project)
              </DropdownMenuItem>
              <div className="px-2 py-1 text-[10px] text-slate-500 uppercase tracking-wider">
                Supports
              </div>
              <div className="px-2 pb-1 text-[10px] text-slate-400 leading-tight">
                .xlsx · .xls — Excel<br />
                .xer — Primavera P6<br />
                .mpp · .mpx · .mpd · .xml — MS Project
              </div>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem 
                onClick={() => {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/scheduling/export/template`;
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'schedule_import_template.xlsx';
                  a.click();
                  toast.success('Downloading schedule import template...');
                }}
                className="text-slate-200 hover:bg-slate-700"
                data-testid="menu-download-template"
              >
                <Download className="w-4 h-4 mr-2 text-amber-400" />
                Download Import Template
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Selection Info */}
        {hasSelection && (
          <div className="flex items-center gap-2 pl-3 border-l border-slate-700/50 text-xs text-slate-500">
            {isMultiSelect ? (
              <span>{selectedTasks.length} tasks selected</span>
            ) : selectedTask && (
              <span>#{selectedTask.uid} {selectedTask.name?.substring(0, 20)}{selectedTask.name?.length > 20 ? '...' : ''}</span>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete Summary Task?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-slate-200">
                Deleting <span className="font-medium text-white">"{taskToDelete?.name}"</span> will also remove:
              </p>
              <ul className="mt-2 text-sm text-slate-400 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  {childCount} child task{childCount !== 1 ? 's' : ''}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  All predecessor/successor links
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  Resource assignments
                </li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Delete {childCount + 1} Tasks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Baseline Dialog */}
      <Dialog open={baselineDialogOpen} onOpenChange={setBaselineDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-[#1E4E8C]" />
              Create Baseline
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Capture a snapshot of the current schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-slate-300">Baseline Name</Label>
              <Input
                value={baselineForm.name}
                onChange={(e) => setBaselineForm({ ...baselineForm, name: e.target.value })}
                className="mt-1.5 bg-slate-900 border-slate-600"
                placeholder="e.g., BL-1, Initial Baseline"
              />
            </div>
            
            <div className="space-y-3">
              <Label className="text-slate-300">Options</Label>
              
              <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg">
                <Checkbox
                  id="freezeAll"
                  checked={baselineForm.freezeAll}
                  onCheckedChange={(checked) => setBaselineForm({ ...baselineForm, freezeAll: !!checked })}
                  className="data-[state=checked]:bg-[#1E4E8C]"
                />
                <div className="flex-1">
                  <label htmlFor="freezeAll" className="text-sm text-white cursor-pointer">Freeze all activities</label>
                  <p className="text-xs text-slate-500">Lock schedule dates after baselining</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg">
                <Checkbox
                  id="includeCompleted"
                  checked={baselineForm.includeCompleted}
                  onCheckedChange={(checked) => setBaselineForm({ ...baselineForm, includeCompleted: !!checked })}
                  className="data-[state=checked]:bg-[#1E4E8C]"
                />
                <div className="flex-1">
                  <label htmlFor="includeCompleted" className="text-sm text-white cursor-pointer">Include completed tasks</label>
                  <p className="text-xs text-slate-500">Baseline tasks with 100% progress</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg">
                <Checkbox
                  id="includeSummary"
                  checked={baselineForm.includeSummary}
                  onCheckedChange={(checked) => setBaselineForm({ ...baselineForm, includeSummary: !!checked })}
                  className="data-[state=checked]:bg-[#1E4E8C]"
                />
                <div className="flex-1">
                  <label htmlFor="includeSummary" className="text-sm text-white cursor-pointer">Include summary rollups</label>
                  <p className="text-xs text-slate-500">Baseline WBS summary tasks</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBaselineDialogOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={confirmCreateBaseline} 
              disabled={!baselineForm.name}
              className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
            >
              Create Baseline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Task Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Move className="w-5 h-5 text-[#1E4E8C]" />
              {moveMode === 'parent' ? 'Move to Parent' : 'Move as Child'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Select the target task to move under.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-300 mb-2 block">Moving: <span className="text-white font-medium">{selectedTask?.name}</span></Label>
            
            <div className="mt-3">
              <Label className="text-slate-300 mb-2 block">Select Target Task:</Label>
              <Select value={targetTaskId} onValueChange={setTargetTaskId}>
                <SelectTrigger className="bg-slate-900 border-slate-600">
                  <SelectValue placeholder="Select a task..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-64">
                  {availableParents.map(task => (
                    <SelectItem key={task.id} value={task.id} className="text-slate-200">
                      <span className="text-[#1E4E8C] font-mono text-xs mr-2">#{task.uid}</span>
                      <span className="text-slate-500 font-mono text-xs mr-2">{task.wbs_code}</span>
                      {task.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={confirmMove} 
              disabled={!targetTaskId}
              className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
            >
              Move Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Baselines Dialog */}
      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-[#1E4E8C]" />
              Compare Baselines
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Compare schedule variance between baselines or against current schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Baseline 1 (Reference)</Label>
              <Select value={compareBaseline1} onValueChange={setCompareBaseline1}>
                <SelectTrigger className="bg-slate-900 border-slate-600">
                  <SelectValue placeholder="Select baseline..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {baselines?.map(bl => (
                    <SelectItem key={bl.baseline_number} value={bl.baseline_number?.toString()} className="text-slate-200">
                      {bl.name} ({new Date(bl.created_at).toLocaleDateString()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300 mb-2 block">Compare Against</Label>
              <Select value={compareBaseline2} onValueChange={setCompareBaseline2}>
                <SelectTrigger className="bg-slate-900 border-slate-600">
                  <SelectValue placeholder="Select comparison..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="current" className="text-slate-200">
                    <span className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-emerald-400" />
                      Current Schedule
                    </span>
                  </SelectItem>
                  {baselines?.filter(bl => bl.baseline_number?.toString() !== compareBaseline1).map(bl => (
                    <SelectItem key={bl.baseline_number} value={bl.baseline_number?.toString()} className="text-slate-200">
                      {bl.name} ({new Date(bl.created_at).toLocaleDateString()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {compareBaseline1 && (
              <div className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-400">
                  This will show variance analysis comparing:
                </p>
                <ul className="mt-2 text-sm space-y-1">
                  <li className="text-white">
                    • {baselines?.find(b => b.baseline_number?.toString() === compareBaseline1)?.name || 'Selected Baseline'}
                  </li>
                  <li className="text-[#1E4E8C]">
                    vs {compareBaseline2 === 'current' ? 'Current Schedule' : baselines?.find(b => b.baseline_number?.toString() === compareBaseline2)?.name}
                  </li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompareDialogOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={confirmCompareBaselines} 
              disabled={!compareBaseline1}
              className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
            >
              <GitCompare className="w-4 h-4 mr-2" />
              Compare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insert Multiple Rows Dialog */}
      <Dialog open={insertRowsDialogOpen} onOpenChange={setInsertRowsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#1E4E8C]" />
              Insert Multiple Rows
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Insert multiple empty rows at the selected position. Existing rows will shift down.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Number of rows to insert</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={insertRowCount}
                onChange={(e) => setInsertRowCount(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                className="bg-slate-900 border-slate-600 text-white"
                placeholder="Enter number of rows..."
              />
            </div>
            
            <div>
              <Label className="text-slate-300 mb-2 block">Insert position</Label>
              <Select value={insertPosition} onValueChange={setInsertPosition}>
                <SelectTrigger className="bg-slate-900 border-slate-600">
                  <SelectValue placeholder="Select position..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="below" className="text-slate-200">
                    <span className="flex items-center gap-2">
                      <ArrowDownRight className="w-4 h-4 text-[#1E4E8C]" />
                      Below selected task
                    </span>
                  </SelectItem>
                  <SelectItem value="above" className="text-slate-200">
                    <span className="flex items-center gap-2">
                      <ArrowUpRight className="w-4 h-4 text-[#1E4E8C]" />
                      Above selected task
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedTask && (
              <div className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-400 mb-1">Reference task:</p>
                <p className="text-white text-sm">
                  <span className="text-[#1E4E8C] font-mono mr-2">#{selectedTask.uid}</span>
                  <span className="text-slate-500 font-mono mr-2">{selectedTask.wbs_code}</span>
                  {selectedTask.name}
                </p>
              </div>
            )}

            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-sm text-blue-300">
                <strong>{insertRowCount}</strong> new row{insertRowCount > 1 ? 's' : ''} will be inserted {insertPosition === 'below' ? 'after' : 'before'} the selected task.
                Existing rows will shift down automatically.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsertRowsDialogOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (selectedTaskId && onInsertRow) {
                  onInsertRow(selectedTaskId, insertPosition, null, insertRowCount);
                  setInsertRowsDialogOpen(false);
                  setInsertRowCount(1); // Reset
                }
              }} 
              disabled={!selectedTaskId || insertRowCount < 1}
              className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Insert {insertRowCount} Row{insertRowCount > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default WBSToolbar;
